/**
 * Traspasar Matrícula — migración web de ares6030.
 *
 * Traspasa un vehículo/matrícula a otro titular. El backend decide la
 * variante según el estado del origen:
 *   - Préstamo ACTIVO con saldo: clona el préstamo al nuevo titular con las
 *     cuotas pendientes (mismas fechas), cierra el viejo y reasigna el
 *     producto. Bloqueado si hay cuotas vencidas impagas.
 *   - Venta al contado o préstamo saldado: solo registra el traspaso.
 * En el escritorio se guardaba al pulsar "Imprimir" y sin transacción; aquí
 * Guardar es transaccional y el comprobante se imprime después.
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";
import { encabezadoEmpresaHtml } from "lib/impresionhtml";

const filtrarCandidatos = createFilterOptions({
  stringify: (o) =>
    typeof o === "string"
      ? o
      : `${o.noprest} ${o.nombres || ""} ${o.chasis || ""} ${o.secuencia || ""}`,
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const formatCedula = (v) => {
  const d = String(v || "")
    .replace(/\D/g, "")
    .slice(0, 11);
  const p1 = d.slice(0, 3);
  const p2 = d.slice(3, 10);
  const p3 = d.slice(10, 11);
  let out = p1;
  if (p2) out += "-" + p2;
  if (p3) out += "-" + p3;
  return out;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

// Comprobante "Cobro de Traspaso" (ares6030.imprimirtraspaso).
function imprimirComprobante(r) {
  const fila = (k, v) =>
    `<tr><td style="padding:3px 8px;color:#555">${k}</td><td style="padding:3px 8px;font-weight:bold">${
      v || ""
    }</td></tr>`;
  const html = `
    <html><head><meta name="color-scheme" content="light"><title>Cobro de Traspaso</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:20px;font-size:13px;max-width:520px;margin:0 auto">
      ${encabezadoEmpresaHtml(r.compania)}
      <p style="text-align:center;margin:0 0 4px;font-weight:bold">Cobro de Traspaso</p>
      <p style="text-align:center;margin:0 0 14px;font-size:11px">${
        r.id ? `Recibo #${r.id} · ` : ""
      }A la fecha ${r.fecha}</p>
      <p style="margin:10px 0 4px;font-weight:bold;border-bottom:1px solid #000">DATOS DEL TRASPASO</p>
      <table style="width:100%;border-collapse:collapse">
        ${fila("Chasis", r.chasis)}
        ${fila("Marca", r.marca)}
        ${fila("Modelo", r.modelo)}
        ${fila("Color", r.colore)}
        ${r.comentario ? fila("Comentario", r.comentario) : ""}
      </table>
      <p style="margin:14px 0 4px;font-weight:bold;border-bottom:1px solid #000">TRASPASAR A:</p>
      <table style="width:100%;border-collapse:collapse">
        ${fila("Cédula", r.tcedula)}
        ${fila("Nombres", r.tnombres)}
        ${fila("Teléfono", r.telefono)}
        ${fila("Calle", r.calle)}
        ${fila("Sector", r.sector)}
        ${fila("Valor", "RD$ " + fmtMoney(r.valor))}
      </table>
      <p style="margin-top:40px;border-top:1px solid #000;padding-top:6px;text-align:center;width:60%;margin-left:auto;margin-right:auto">Revisado Por</p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

const DESTINO_INICIAL = {
  tcedula: "",
  tnombres: "",
  tapellidos: "",
  apodo: "",
  telefono: "",
  calle: "",
  sector: "",
  valor: "",
  fiador: "",
  fiadorced: "",
  fiadortel: "",
  fiadordir: "",
  comentario: "",
  tipodepago: "Contado",
  descripcionpago: "",
};

const ETIQUETA_VARIANTE = {
  financiado: {
    texto: "Préstamo activo — se creará un préstamo nuevo al destinatario",
    color: "#E3F2FD",
    tcolor: "#1565C0",
  },
  saldado: {
    texto: "Préstamo saldado — solo se registra el traspaso",
    color: "#E8F5E9",
    tcolor: "#2E7D32",
  },
  contado: {
    texto: "Venta al contado — solo se registra el traspaso",
    color: "#E8F5E9",
    tcolor: "#2E7D32",
  },
};

export default function TraspasarMatricula({ embedded = false, onExit, onSaved, chasisInicial } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [opts, setOpts] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [sel, setSel] = useState(null);
  const [origen, setOrigen] = useState(null);
  const [cargando, setCargando] = useState(false);

  const [d, setD] = useState(DESTINO_INICIAL);
  const setF = (k) => (e) => setD((p) => ({ ...p, [k]: e.target.value }));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const cargarCandidatos = () => {
    setBuscando(true);
    return post("/api/traspasomatricula_candidatos", {})
      .then(({ ok, data }) => {
        if (ok && Array.isArray(data.candidatos)) setOpts(data.candidatos);
        else
          showAlert({
            message: data.Error || "No se pudieron cargar las facturas",
            color: "error",
          });
      })
      .finally(() => setBuscando(false));
  };

  useEffect(() => {
    cargarCandidatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const limpiar = () => {
    setSel(null);
    setOrigen(null);
    setD(DESTINO_INICIAL);
    cargarCandidatos();
  };

  // Preselección: al abrir desde el expediente ya viene el chasis del préstamo.
  const [preselListo, setPreselListo] = useState(false);
  useEffect(() => {
    if (!chasisInicial || preselListo || !opts.length) return;
    const opcion = opts.find((x) => String(x.chasis) === String(chasisInicial));
    if (opcion) {
      setSel(opcion);
      cargarOrigen(opcion);
    }
    setPreselListo(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts]);

  const cargarOrigen = async (cand) => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/traspasomatricula_datos", {
        noprest: String(cand.noprest ?? "0"),
        chasis: cand.chasis,
        secuencia: String(cand.secuencia ?? ""),
      });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudieron cargar los datos", color: "error" });
        setSel(null);
        setOrigen(null);
        return;
      }
      setOrigen(data);
      if (data.bloqueadoporatraso) {
        showAlert({
          message: "Este préstamo tiene atraso de cuotas, no se puede traspasar",
          color: "error",
        });
      }
    } finally {
      setCargando(false);
    }
  };

  const validar = () => {
    if (!sel || !origen) {
      showAlert({ message: "Seleccione el préstamo o factura de origen", color: "warning" });
      return false;
    }
    if (origen.bloqueadoporatraso) {
      showAlert({
        message: "Este préstamo tiene atraso de cuotas, no se puede traspasar",
        color: "error",
      });
      return false;
    }
    if (!d.tcedula.trim() || d.tcedula.trim().length < 11) {
      showAlert({ message: "La cédula parece no estar completa", color: "warning" });
      return false;
    }
    if (d.tcedula.trim() === (origen.cedula || "").trim()) {
      showAlert({
        message: "La cédula no puede ser igual a la del préstamo original",
        color: "warning",
      });
      return false;
    }
    const obligatorios = [
      [d.tnombres, "nombres"],
      [d.tapellidos, "apellidos"],
      [d.apodo, "apodo"],
      [d.telefono, "teléfono"],
      [d.calle, "calle"],
      [d.sector, "sector"],
      [d.fiador, "fiador"],
      [d.fiadorced, "cédula del fiador"],
      [d.fiadortel, "teléfono del fiador"],
      [d.fiadordir, "dirección del fiador"],
    ];
    for (const [v, nombre] of obligatorios) {
      if (!v.trim()) {
        showAlert({ message: `El campo ${nombre} no puede estar en blanco`, color: "warning" });
        return false;
      }
    }
    if (toNum(d.valor) <= 0) {
      showAlert({ message: "El valor del traspaso no puede estar en 0", color: "warning" });
      return false;
    }
    return true;
  };

  const guardar = async () => {
    setConfirmOpen(false);
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/traspasomatricula_guardar", {
        noprest: String(sel.noprest ?? "0"),
        chasis: origen.chasis,
        tcedula: d.tcedula.trim(),
        tnombres: d.tnombres.trim(),
        tapellidos: d.tapellidos.trim(),
        apodo: d.apodo.trim(),
        telefono: d.telefono.trim(),
        calle: d.calle.trim(),
        sector: d.sector.trim(),
        valor: toNum(d.valor),
        fiador: d.fiador.trim(),
        fiadorced: d.fiadorced.trim(),
        fiadortel: d.fiadortel.trim(),
        fiadordir: d.fiadordir.trim(),
        comentario: d.comentario.trim(),
        tipodepago: d.tipodepago,
        descripcionpago: d.descripcionpago.trim(),
      });
      if (ok) {
        showAlert({
          message:
            (data.mensajeok || "Traspaso registrado") +
            (data.noprestnuevo ? ` — préstamo nuevo #${data.noprestnuevo}` : ""),
          color: "success",
        });
        if (data.recibo) imprimirComprobante(data.recibo);
        limpiar();
        if (onSaved) onSaved();
      } else {
        showAlert({ message: data.Error || "Error al registrar el traspaso", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  const etiqueta = origen ? ETIQUETA_VARIANTE[origen.variante] : null;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Traspasar Matrícula"
        subtitle="Traspasa un vehículo a otro titular — si el préstamo está activo, la deuda pendiente pasa al nuevo dueño"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nuevo
            </Button>
            <Button
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={() => validar() && setConfirmOpen(true)}
              disabled={guardando || !origen || origen.bloqueadoporatraso}
              sx={btnSolid(FP.color.navy, FP.color.navySoft)}
            >
              Guardar traspaso
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Origen */}
        <Box sx={sectionBox}>
          <SectionTitle icon={DirectionsCarFilledOutlinedIcon} color={FP.color.accentBlue}>
            Préstamo / Vehículo de origen
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={5}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarCandidatos}
                options={opts}
                loading={buscando || cargando}
                value={sel}
                getOptionLabel={(o) =>
                  typeof o === "string" ? o : `${o.chasis} — ${o.nombres || ""}`
                }
                isOptionEqualToValue={(o, v) => (o.secuencia || o) === (v.secuencia || v)}
                onChange={(_, v) => {
                  setSel(v);
                  setOrigen(null);
                  if (v?.chasis) cargarOrigen(v);
                }}
                renderOption={(props, o) => (
                  <li {...props} key={`${o.secuencia}`}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.chasis}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.noprest ? `Prest. ${o.noprest}` : "Contado", o.nombres]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Chasis, préstamo o cliente" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                label="Cédula"
                value={origen?.cedula || ""}
                InputProps={{ readOnly: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={3}>
              <TextField
                fullWidth
                label="Titular actual"
                value={origen ? [origen.nombres, origen.apellidos].filter(Boolean).join(" ") : ""}
                InputProps={{ readOnly: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                fullWidth
                label="Vehículo"
                value={origen ? [origen.marca, origen.modelo].filter(Boolean).join(" ") : ""}
                InputProps={{ readOnly: true }}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
          {origen && (
            <Stack direction="row" spacing={1.2} sx={{ mt: 1.5 }} flexWrap="wrap">
              {etiqueta && (
                <Chip
                  label={etiqueta.texto}
                  sx={{ bgcolor: etiqueta.color, color: etiqueta.tcolor, fontWeight: 700 }}
                />
              )}
              {origen.variante === "financiado" && (
                <Chip
                  label={`Balance pendiente: RD$ ${fmtMoney(origen.balance)}`}
                  sx={{
                    bgcolor: FP.color.surfaceAlt,
                    color: FP.color.textSecondary,
                    fontWeight: 700,
                  }}
                />
              )}
              {origen.bloqueadoporatraso && (
                <Chip
                  label="Atraso de cuotas — traspaso bloqueado"
                  sx={{ bgcolor: FP.color.dangerSoft, color: FP.color.danger, fontWeight: 700 }}
                />
              )}
            </Stack>
          )}
        </Box>

        {/* Nuevo titular */}
        <Box sx={sectionBox}>
          <SectionTitle icon={PersonOutlineIcon} color={FP.color.info}>
            Traspasar a
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Cédula"
                value={d.tcedula}
                onChange={(e) => setD((p) => ({ ...p, tcedula: formatCedula(e.target.value) }))}
                placeholder="000-0000000-0"
                inputProps={{ maxLength: 13 }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Nombres"
                value={d.tnombres}
                onChange={setF("tnombres")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Apellidos"
                value={d.tapellidos}
                onChange={setF("tapellidos")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Apodo"
                value={d.apodo}
                onChange={setF("apodo")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Teléfono"
                value={d.telefono}
                onChange={setF("telefono")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={5}>
              <TextField
                fullWidth
                label="Calle"
                value={d.calle}
                onChange={setF("calle")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <TextField
                fullWidth
                label="Sector"
                value={d.sector}
                onChange={setF("sector")}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Fiador */}
        <Box sx={sectionBox}>
          <SectionTitle icon={GroupOutlinedIcon} color={FP.color.info}>
            Fiador
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Nombre"
                value={d.fiador}
                onChange={setF("fiador")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Cédula"
                value={d.fiadorced}
                onChange={setF("fiadorced")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Teléfono"
                value={d.fiadortel}
                onChange={setF("fiadortel")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Dirección"
                value={d.fiadordir}
                onChange={setF("fiadordir")}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Valor y pago */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <SectionTitle icon={PaymentsOutlinedIcon} color={FP.color.success}>
            Valor y Pago
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                type="number"
                label="Valor del traspaso"
                value={d.valor}
                onChange={setF("valor")}
                InputProps={{
                  startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                }}
                sx={fpInputSx}
              />
            </Grid>
            {origen?.variante !== "saldado" && (
              <>
                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    fullWidth
                    label="Tipo de pago"
                    value={d.tipodepago}
                    onChange={setF("tipodepago")}
                    sx={fpInputSx}
                  >
                    <MenuItem value="Contado">Contado</MenuItem>
                    <MenuItem value="Transferencias">Transferencias</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Referencia del pago"
                    value={d.descripcionpago}
                    onChange={setF("descripcionpago")}
                    sx={fpInputSx}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Comentario"
                value={d.comentario}
                onChange={setF("comentario")}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Confirmación */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Confirmar traspaso</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {origen?.variante === "financiado" ? (
              <>
                Se traspasará el chasis <b>{origen?.chasis}</b> a{" "}
                <b>
                  {d.tnombres} {d.tapellidos}
                </b>
                : se CERRARÁ el préstamo actual y se creará uno nuevo con el balance pendiente de
                RD$ <b>{fmtMoney(origen?.balance)}</b> y las cuotas restantes. ¿Continuar?
              </>
            ) : (
              <>
                Se registrará el traspaso del chasis <b>{origen?.chasis}</b> a{" "}
                <b>
                  {d.tnombres} {d.tapellidos}
                </b>{" "}
                por RD$ <b>{fmtMoney(toNum(d.valor))}</b>. ¿Continuar?
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={btnGhost}>
            No
          </Button>
          <Button onClick={guardar} sx={btnSolid(FP.color.navy, FP.color.navySoft)}>
            Sí, traspasar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
